import { isEqual } from 'lodash'
import { useCallback, useEffect, useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { APICall, CustomAxiosConfig, PaginatedCall } from 'src/services/skuit/types'
import { PaginatedResponse } from '../../api/models'
import { queryClient } from '../../App'

const INITIAL_PAGE = 1
const DEFAULT_PAGE_SIZE = 100
const MAX_PAGE_SIZE = 500

export type Params = { [key: string]: string | number | boolean | null | undefined }
export type Setter = (newValues: { [key: string]: string | number | boolean | null | undefined }) => void

export interface Paginator {
  loading: boolean
  fetching: boolean
  data: {
    count: number
    results: any[]
    next?: string | null
  }
  page: number
  pageSize: number | 'all'
  firstIndex: number
  lastIndex: number
  handlePageChange: (event: React.ChangeEvent<any>, newPage: number) => void
  handleSearchChange?: (event?: React.ChangeEvent<any>, query?: string) => void
  filters: Params
  setFilters: Setter
  patchFilters: Setter
  resetFilters: Setter
  refresh: () => void
  loadingAll?: boolean
  fetchAllReady?: boolean
}

export class ClassicPaginator {
  call: PaginatedCall
  loading: boolean
  fetching: boolean
  data: {
    count: number
    results: any[]
    next?: string | null
  }
  page: number
  pageSize: number | 'all'
  firstIndex: number
  lastIndex: number
  filters: Params
  loadingAll?: boolean
  fetchAllReady?: boolean
  initialFilters: Params

  constructor(call: PaginatedCall, defaultParams: Params) {
    this.initialFilters = { page: INITIAL_PAGE , page_size: DEFAULT_PAGE_SIZE, search: '', ...defaultParams }
    this.call = call
    this.page = this.initialFilters.page as number
    this.pageSize = this.initialFilters.page_size as number
    this.data = { count: 0, results: [] }
    this.fetching = false
    this.loading = false
    this.firstIndex = 1
    this.lastIndex = 1
    this.filters = {...this.initialFilters}
    this.refresh()
  }

  handlePageChange = (event: React.ChangeEvent<any>, newPage: number) => {
    this.filters.page = newPage
    this.refresh()
  }

  handleSearchChange = (event?: React.ChangeEvent<any>, query?: string) => {
  }

  setFilters = (newValues: { [key: string]: string | number | null | undefined }) => {
    this.filters = {...newValues}
    this.refresh()
  }

  patchFilters = (newValues: { [key: string]: string | number | null | undefined }) => {
    this.filters = {...this.filters, ...newValues}
    this.refresh()
  }

  resetFilters = () => {
    this.filters = {...this.initialFilters}
    this.refresh()
  }

  refresh = () => {
    this.loading = true
    this.fetching = true
    this.call({ params: this.filters }).finally(() => {
      this.loading = false
      this.fetching = false
    })
  }
}

export const usePaginator = (getCurrentPage: APICall, config?: CustomAxiosConfig): Paginator => {
  const axiosConfig = { ...(config || {}) }
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  axiosConfig.params = { ...(axiosConfig.params || {}), page, search }

  const pageSize = config?.params?.page_size || DEFAULT_PAGE_SIZE //  eslint-disable-line camelcase

  const [{ loading, data }, refresh] = getCurrentPage(axiosConfig)

  // Calculate the starting and ending index on the page
  const numOnPage = data?.results?.length || 0
  const firstIndex = pageSize !== 'all' ? pageSize * (page - 1) + 1 : 1
  const lastIndex = pageSize !== 'all' ? pageSize * (page - 1) + numOnPage : data.results.length

  return {
    loading,
    fetching: loading,
    data,
    page,
    pageSize,
    firstIndex,
    lastIndex,
    handlePageChange: (_: React.ChangeEvent<any>, newPage: number) => {
      setPage(newPage)
    },
    handleSearchChange: (_?: React.ChangeEvent<any>, query?: string) => {
      if (query !== undefined) {
        setSearch(query)
      }
    },
    filters: {},
    setFilters: () => {},
    patchFilters: () => {},
    resetFilters: () => {},
    refresh,
  }
}

export const createPaginator = (
  loading: boolean,
  data: any,
  page: number,
  setPage: (page: number) => void
) => {
  return {
    loading,
    data,
    page,
    handlePageChange: (_: React.ChangeEvent<any>, newPage: number) => {
      setPage(newPage)
    },
  }
}

export const useParams = (defaultParams: Params = {}): [Params, Setter, Setter, Setter] => {
  const [initialParams, setInitialParams] = useState<Params>(defaultParams)
  const [fullInitialParams, setFullInitialParams] = useState<Params>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    search: '',
    ...defaultParams,
  })

  const [params, setParams] = useState<Params>(fullInitialParams)
  useEffect(() => {
    if (!isEqual(defaultParams, initialParams)) {
      setInitialParams(defaultParams)
      const newParams = { page: 1, page_size: DEFAULT_PAGE_SIZE, search: '', ...defaultParams }
      setFullInitialParams(newParams)
      setParams(newParams)
    }
  }, [defaultParams, initialParams, fullInitialParams])

  const patchParams = useCallback<Setter>((newValues) => {
    setParams((previousParams) => ({ ...previousParams, ...newValues }))
  }, [])

  const resetParams = useCallback<Setter>(() => {
    setParams(fullInitialParams)
  }, [fullInitialParams])

  return [params, setParams, patchParams, resetParams]
}

const useFetchAll = (
  getData: (config?: CustomAxiosConfig) => Promise<PaginatedResponse<any>>,
  params: any,
  config?: CustomAxiosConfig,
) => {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<any[]>()
  const [baseConfig] = useState(config)
  const dataFetcher = useCallback(getData, [])

  const fetchAll = useCallback(async () => {
    let currentPage = 1
    let continueFetching = true
    let totalFetchedItems: any[] = []

    while (continueFetching) {
      const configApi = {
        ...baseConfig,
        params: {
          ...baseConfig?.params,
          ...params,
          page: currentPage,
          page_size: MAX_PAGE_SIZE,
        },
      }
      const data = await dataFetcher(configApi)

      totalFetchedItems = [...totalFetchedItems, ...data.results]
      continueFetching = !!data.next

      currentPage++
    }

    setData(totalFetchedItems)
    setIsLoading(false)
  }, [baseConfig, params, dataFetcher])

  const page = params.page
  useEffect(() => {
    if (page === 'all') {
      fetchAll()
    }
  }, [page, fetchAll]) 

  return {
    isLoading,
    data,
  }
}

export const useQueryPaginator = (
  key: string,
  getData: (config?: CustomAxiosConfig) => Promise<PaginatedResponse<any>>,
  config?: CustomAxiosConfig,
  useQueryConfig?: any
): Paginator => {
  const [stateKey] = useState(key)
  const [params, setParams, patchParams, resetParams] = useParams(config?.params)
  const apiConfig = useMemo(() => ({ ...config, params }), [config, params])
  const configCopy = { ...useQueryConfig }
  if (params.page === 'all') {
    configCopy.enabled = false
  }
  const { isLoading, isFetching, data } = useQuery(
    [stateKey, params],
    () => getData(apiConfig).then((data) => data),
    configCopy
  )

  const { isLoading: fetchAllLoading, data: totalItems } = useFetchAll(getData, params, config)

  const [paginator, setPaginator] = useState<Paginator>({
    loading: true,
    fetching: true,
    data: { count: 0, results: [] },
    page: INITIAL_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    firstIndex: 1,
    lastIndex: 1,
    handlePageChange: () => {},
    handleSearchChange: () => {},
    filters: params,
    setFilters: () => {},
    patchFilters: () => {},
    resetFilters: () => {},
    refresh: () => {},
    loadingAll: fetchAllLoading,
    fetchAllReady: !fetchAllLoading,
  })

  useEffect(() => {
    const page = (params.page || INITIAL_PAGE) as number
    const pageSize = (params.page_size || DEFAULT_PAGE_SIZE) as number | 'all'
    const numOnPage = data?.results?.length || 0
    const firstIndex = pageSize !== 'all' ? pageSize * (page - 1) + 1 : 1
    const lastIndex = pageSize !== 'all' ? pageSize * (page - 1) + numOnPage : numOnPage
    setPaginator({
      loading: isLoading,
      fetching: isFetching,
      data: !fetchAllLoading && totalItems?.length
        ? {
            count: totalItems.length || 0,
            results: totalItems,
          }
        : data || {
            count: 0,
            results: [],
          },
      page,
      pageSize,
      firstIndex,
      lastIndex,
      handlePageChange: (_: React.ChangeEvent<any>, newPage: number) => {
        patchParams({ page: newPage })
      },
      handleSearchChange: (_?: React.ChangeEvent<any>, query?: string) => {
        if (query !== undefined) {
          patchParams({ search: query })
        }
      },
      filters: params,
      setFilters: setParams,
      patchFilters: patchParams,
      resetFilters: resetParams,
      refresh: () => {
        queryClient.invalidateQueries([stateKey, params])
      },
      loadingAll: fetchAllLoading,
      fetchAllReady: !fetchAllLoading,
    })
  }, [
    params,
    patchParams,
    resetParams,
    setParams,
    isLoading,
    isFetching,
    data,
    stateKey,
    fetchAllLoading,
    totalItems,
  ])

  return paginator
}
